import { relations } from 'drizzle-orm'
import { index, integer, real, sqliteTable, text } from 'drizzle-orm/sqlite-core'

import { generateUuid } from '../utils/uuid'

// Type definitions for JSON columns
export type PhoneNumberCapabilities = {
	voice: boolean
	sms: boolean
}

export const Grids = sqliteTable('grids', {
	id: integer('id').primaryKey({ autoIncrement: true }),
	uuid: text('uuid')
		.unique()
		.$defaultFn(() => generateUuid())
		.notNull(),
	name: text('name').notNull(),
	created_at: text('created_at')
		.$defaultFn(() => new Date().toISOString())
		.notNull(),
	updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	status: text('status').notNull(),
	auto_update: integer('auto_update', { mode: 'boolean' }).notNull().default(false),
	current_version_id: integer('current_version_id'),
	pending_version_id: integer('pending_version_id'),
})

export const Locations = sqliteTable('locations', {
	id: integer('id').primaryKey({ autoIncrement: true }),
	uuid: text('uuid')
		.unique()
		.$defaultFn(() => generateUuid())
		.notNull(),
	address: text('address').notNull(),
	phone_number: text('phone_number').notNull(),
	latitude: real('latitude').notNull(),
	longitude: real('longitude').notNull(),
	name: text('name').notNull(),
	place_id: text('place_id').notNull(),
	rating: real('rating').notNull(),
	url: text('url').notNull(),
	ratings_total: integer('ratings_total').notNull(),
	website: text('website'),
	types: text('types', { mode: 'json' }).$type<string[]>().notNull(),
	created_at: text('created_at')
		.$defaultFn(() => new Date().toISOString())
		.notNull(),
	updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
})

export const OpeningHoursPeriods = sqliteTable('opening_hours_periods', {
	id: integer('id').primaryKey({ autoIncrement: true }),
	uuid: text('uuid')
		.unique()
		.$defaultFn(() => generateUuid())
		.notNull(),
	location_id: integer('location_id').notNull(),
	open_day: integer('open_day').notNull(),
	open_time: text('open_time').notNull(),
	close_day: integer('close_day').notNull(),
	close_time: text('close_time').notNull(),
})

export const Photos = sqliteTable('photos', {
	id: integer('id').primaryKey({ autoIncrement: true }),
	uuid: text('uuid')
		.unique()
		.$defaultFn(() => generateUuid())
		.notNull(),
	location_id: integer('location_id').notNull(),
	height: integer('height').notNull(),
	width: integer('width').notNull(),
	photo_reference: text('photo_reference').notNull(),
})

export const Reviews = sqliteTable('reviews', {
	id: integer('id').primaryKey({ autoIncrement: true }),
	uuid: text('uuid')
		.unique()
		.$defaultFn(() => generateUuid())
		.notNull(),
	location_id: integer('location_id').notNull(),
	author_name: text('author_name').notNull(),
	author_url: text('author_url').notNull(),
	language: text('language').notNull(),
	original_language: text('original_language').notNull(),
	profile_photo_url: text('profile_photo_url').notNull(),
	rating: real('rating').notNull(),
	relative_time_description: text('relative_time_description').notNull(),
	text: text('text').notNull(),
	time: integer('time').notNull(),
	translated: integer('translated', { mode: 'boolean' }).notNull(),
})

/**
 * Flexible Connections table supporting multiple auth mechanisms
 * Replaces the global D1 connections table with workspace-scoped storage
 */
export const Connections = sqliteTable('connections', {
	id: integer('id').primaryKey({ autoIncrement: true }),
	uuid: text('uuid')
		.unique()
		.$defaultFn(() => generateUuid())
		.notNull(),
	workspace_id: text('workspace_id').notNull(),
	name: text('name').notNull(),
	platform: text('platform', {
		enum: ['google_ads', 'meta_ads', 'stripe', 'twilio', 'cloudflare', 'openai'],
	}).notNull(),
	auth_type: text('auth_type', {
		enum: ['oauth2', 'api_key', 'basic_auth', 'jwt', 'webhook_secret'],
	}).notNull(),
	status: text('status', {
		enum: ['pending', 'active', 'inactive', 'expired', 'error'],
	}).notNull(),

	// Managed flag: true = HoneyGrid manages this connection, false = user manages
	managed: integer('managed', { mode: 'boolean' }).notNull().default(false),

	// Flexible auth storage (encrypted JSON)
	auth_data: text('auth_data', { mode: 'json' }).notNull(),
	platform_data: text('platform_data', { mode: 'json' }),
	settings: text('settings', { mode: 'json' }),

	// Audit fields
	created_by: text('created_by').notNull(),
	created_at: text('created_at')
		.$defaultFn(() => new Date().toISOString())
		.notNull(),
	updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	last_synced_at: text('last_synced_at'),
	expires_at: text('expires_at'),
})

export const DataSources = sqliteTable('data_sources', {
	id: integer('id').primaryKey({ autoIncrement: true }),
	uuid: text('uuid')
		.unique()
		.$defaultFn(() => generateUuid())
		.notNull(),
	workspace_id: text('workspace_id').notNull(),
	name: text('name').notNull(),
	type: text('type', { enum: ['csv', 'google_ads'] }).notNull(),
	status: text('status', { enum: ['processing', 'failed', 'ready', 'pending'] }).notNull(),
	status_message: text('status_message'),
	total_cells: integer('total_cells').default(0).notNull(),
	file_path: text('file_path'),
	connection_id: integer('connection_id'), // References Connections table (FK removed per DO pattern)
	current_version_id: integer('current_version_id'),
	workflow_id: text('workflow_id'),
	created_at: text('created_at')
		.$defaultFn(() => new Date().toISOString())
		.notNull(),
	updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	completed_at: text('completed_at'),
})

export const DataSourceVersions = sqliteTable('data_source_versions', {
	id: integer('id').primaryKey({ autoIncrement: true }),
	uuid: text('uuid')
		.unique()
		.$defaultFn(() => generateUuid())
		.notNull(),
	data_source_id: integer('data_source_id').notNull(),
	version_number: integer('version_number').notNull(),
	file_path: text('file_path'),
	status: text('status', { enum: ['processing', 'failed', 'ready', 'pending'] })
		.notNull()
		.default('pending'),
	status_message: text('status_message'),
	total_cells: integer('total_cells').default(0).notNull(),
	workflow_id: text('workflow_id'),
	kv_data_key: text('kv_data_key'),
	created_at: text('created_at')
		.$defaultFn(() => new Date().toISOString())
		.notNull(),
	updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	completed_at: integer(),
	metadata: text('metadata', { mode: 'json' }),
})

export const GridVersions = sqliteTable('grid_versions', {
	id: integer('id').primaryKey({ autoIncrement: true }),
	uuid: text('uuid')
		.unique()
		.$defaultFn(() => generateUuid())
		.notNull(),
	grid_id: integer('grid_id').notNull(),
	name: text('name').notNull(),
	version_number: integer('version_number').notNull(),
	status: text('status', { enum: ['active', 'pending', 'archived'] }).notNull(),
	households: integer('households'),
	targeted_households: integer('targeted_households'),
	targeting_type: text('targeting_type'),
	targeting_coverage: integer('targeting_coverage'),
	targeted_areas: text('targeted_areas', { mode: 'json' }),
	targeting_filters: text('targeting_filters', { mode: 'json' }),
	manual_inclusion_filters: text('manual_inclusion_filters', { mode: 'json' }),
	manual_exclusion_filters: text('manual_exclusion_filters', { mode: 'json' }),
	last_used_at: text('last_used_at')
		.$defaultFn(() => new Date().toISOString())
		.notNull(),
	created_at: text('created_at')
		.$defaultFn(() => new Date().toISOString())
		.notNull(),
	updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
})

// JOIN TABLE grid_version_data_sources
export const GridVersionDataSources = sqliteTable(
	'grid_version_data_sources',
	{
		id: integer('id').primaryKey({ autoIncrement: true }),
		grid_version_id: integer('grid_version_id').notNull(),
		data_source_id: integer('data_source_id').notNull(),
		data_source_version_id: integer('data_source_version_id').notNull(),
	},
	(t) => [index('gvd_idx').on(t.grid_version_id, t.data_source_id)]
)

export const GridDataSources = sqliteTable(
	'grid_data_sources',
	{
		id: integer('id').primaryKey({ autoIncrement: true }),
		grid_id: integer('grid_id').notNull(),
		data_source_id: integer('data_source_id').notNull(),
	},
	(t) => [index('gd_idx').on(t.grid_id, t.data_source_id)]
)

export const PhoneNumbers = sqliteTable(
	'phone_numbers',
	{
		id: integer('id').primaryKey({ autoIncrement: true }),
		uuid: text('uuid')
			.unique()
			.$defaultFn(() => generateUuid())
			.notNull(),
		phone_number: text('phone_number').notNull(), // E.164 format (+14155551234)
		twilio_sid: text('twilio_sid').notNull(), // Twilio phone number SID for operations
		friendly_name: text('friendly_name').notNull(), // Display name
		status: text('status').notNull().default('active'), // active, inactive, released
		service_type: text('service_type').notNull().default('workspace'), // workspace, campaign, etc.
		assigned_to_workspace_id: text('assigned_to_workspace_id').notNull(), // Which workspace owns this number
		area_code: text('area_code').notNull(), // 3-digit area code for filtering
		capabilities: text('capabilities', { mode: 'json' }).$type<PhoneNumberCapabilities>().notNull(), // Twilio capabilities object

		// Webhook configuration
		voice_webhook_url: text('voice_webhook_url'), // TwiML webhook for voice calls
		sms_webhook_url: text('sms_webhook_url'), // Webhook for SMS
		status_callback_url: text('status_callback_url'), // Call status updates

		// Recording settings
		record_calls: integer('record_calls', { mode: 'boolean' }).notNull().default(true),
		transcribe_calls: integer('transcribe_calls', { mode: 'boolean' }).notNull().default(false),

		// Activity tracking
		last_call_at: text('last_call_at'),
		last_sms_at: text('last_sms_at'),
		total_calls: integer('total_calls').notNull().default(0),
		total_sms: integer('total_sms').notNull().default(0),

		provisioned_at: text('provisioned_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		created_at: text('created_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	},
	(t) => [index('phone_numbers_workspace_idx').on(t.assigned_to_workspace_id)]
)

/**
 * Call logs for tracking all Twilio voice calls
 * Stores technical Twilio data - business context lives in Activities table
 */
export const CallLogs = sqliteTable(
	'call_logs',
	{
		id: integer('id').primaryKey({ autoIncrement: true }),
		uuid: text('uuid')
			.unique()
			.$defaultFn(() => generateUuid())
			.notNull(),
		phone_number_id: integer('phone_number_id').notNull(),
		workspace_id: text('workspace_id').notNull(),

		// Twilio identifiers
		call_sid: text('call_sid').notNull().unique(),
		parent_call_sid: text('parent_call_sid'), // For forwarded calls

		// Call details
		direction: text('direction', { enum: ['inbound', 'outbound'] }).notNull(),
		from_number: text('from_number').notNull(),
		to_number: text('to_number').notNull(),
		status: text('status', {
			enum: ['queued', 'ringing', 'in-progress', 'completed', 'busy', 'failed', 'no-answer', 'canceled'],
		}).notNull(),

		// Call metrics
		duration: integer('duration'), // Call duration in seconds
		answered_by: text('answered_by'), // human, machine, fax, unknown

		// Recording reference
		has_recording: integer('has_recording', { mode: 'boolean' }).notNull().default(false),

		// Timestamps
		start_time: text('start_time'),
		end_time: text('end_time'),
		created_at: text('created_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	},
	(t) => [
		index('call_logs_sid_idx').on(t.call_sid),
		index('call_logs_workspace_idx').on(t.workspace_id, t.created_at),
		index('call_logs_phone_idx').on(t.phone_number_id),
	]
)

/**
 * Call recordings from Twilio
 * Stores recording metadata and URLs
 */
export const CallRecordings = sqliteTable(
	'call_recordings',
	{
		id: integer('id').primaryKey({ autoIncrement: true }),
		uuid: text('uuid')
			.unique()
			.$defaultFn(() => generateUuid())
			.notNull(),
		call_log_id: integer('call_log_id').notNull(),
		phone_number_id: integer('phone_number_id').notNull(),
		workspace_id: text('workspace_id').notNull(),

		// Twilio identifiers
		call_sid: text('call_sid').notNull(),
		recording_sid: text('recording_sid').notNull().unique(),

		// Recording metadata
		recording_url: text('recording_url').notNull(), // Twilio recording URL
		recording_duration: integer('recording_duration'), // Duration in seconds
		recording_status: text('recording_status', {
			enum: ['in-progress', 'completed', 'absent', 'failed'],
		}).notNull(),

		// Storage
		stored_url: text('stored_url'), // R2/S3 URL after download
		transcription: text('transcription'), // Transcription text if enabled

		// Audit
		created_at: text('created_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	},
	(t) => [
		index('call_recordings_sid_idx').on(t.recording_sid),
		index('call_recordings_call_idx').on(t.call_log_id),
	]
)

/**
 * SMS messages sent/received via Twilio
 * Stores technical Twilio data - business context lives in Activities table
 */
export const SmsMessages = sqliteTable(
	'sms_messages',
	{
		id: integer('id').primaryKey({ autoIncrement: true }),
		uuid: text('uuid')
			.unique()
			.$defaultFn(() => generateUuid())
			.notNull(),
		phone_number_id: integer('phone_number_id').notNull(),
		workspace_id: text('workspace_id').notNull(),

		// Twilio identifiers
		message_sid: text('message_sid').notNull().unique(),

		// Message details
		direction: text('direction', { enum: ['inbound', 'outbound'] }).notNull(),
		from_number: text('from_number').notNull(),
		to_number: text('to_number').notNull(),
		body: text('body').notNull(),
		status: text('status', {
			enum: ['queued', 'sending', 'sent', 'delivered', 'undelivered', 'failed'],
		}).notNull(),

		// Media attachments
		num_media: integer('num_media').notNull().default(0),
		media_urls: text('media_urls', { mode: 'json' }), // Array of media URLs

		// Timestamps
		sent_at: text('sent_at'),
		delivered_at: text('delivered_at'),
		created_at: text('created_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	},
	(t) => [
		index('sms_messages_sid_idx').on(t.message_sid),
		index('sms_messages_workspace_idx').on(t.workspace_id, t.created_at),
		index('sms_messages_phone_idx').on(t.phone_number_id),
	]
)

/**
 * Email messages - stores inbound/outbound email communications
 * Similar to SmsMessages and CallLogs, provides detailed email tracking
 */
export const EmailMessages = sqliteTable(
	'email_messages',
	{
		id: integer('id').primaryKey({ autoIncrement: true }),
		uuid: text('uuid')
			.unique()
			.$defaultFn(() => generateUuid())
			.notNull(),
		workspace_id: text('workspace_id').notNull(),

		// Message identifiers
		message_id: text('message_id').notNull().unique(), // Email Message-ID header

		// Message details
		direction: text('direction', { enum: ['inbound', 'outbound'] }).notNull(),
		from_address: text('from_address').notNull(),
		to_address: text('to_address').notNull(),
		subject: text('subject'),
		text_body: text('text_body'),
		html_body: text('html_body'),

		// Email headers (full headers as JSON)
		headers: text('headers', { mode: 'json' }),

		// Attachments
		has_attachments: integer('has_attachments', { mode: 'boolean' }).notNull().default(false),
		attachment_count: integer('attachment_count').notNull().default(0),

		// Status tracking
		status: text('status', {
			enum: ['received', 'sent', 'failed', 'bounced'],
		}).notNull(),

		// Timestamps
		sent_at: text('sent_at'),
		received_at: text('received_at'),
		created_at: text('created_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	},
	(t) => [
		index('email_messages_message_id_idx').on(t.message_id),
		index('email_messages_workspace_idx').on(t.workspace_id, t.created_at),
		index('email_messages_from_idx').on(t.from_address),
		index('email_messages_to_idx').on(t.to_address),
	]
)

/**
 * Webhook events from Twilio for audit trail
 * Stores raw webhook payloads for debugging and compliance
 */
export const WebhookEvents = sqliteTable(
	'webhook_events',
	{
		id: integer('id').primaryKey({ autoIncrement: true }),
		uuid: text('uuid')
			.unique()
			.$defaultFn(() => generateUuid())
			.notNull(),
		workspace_id: text('workspace_id').notNull(),
		phone_number_id: integer('phone_number_id'),

		// Webhook metadata
		event_type: text('event_type').notNull(), // call.initiated, call.completed, sms.received, etc.
		twilio_sid: text('twilio_sid').notNull(), // CallSid or MessageSid

		// Full webhook payload
		payload: text('payload', { mode: 'json' }).notNull(),

		// Processing status
		processed: integer('processed', { mode: 'boolean' }).notNull().default(false),
		error_message: text('error_message'),

		// Timestamps
		received_at: text('received_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		processed_at: text('processed_at'),
	},
	(t) => [
		index('webhook_events_workspace_idx').on(t.workspace_id, t.received_at),
		index('webhook_events_sid_idx').on(t.twilio_sid),
		index('webhook_events_processed_idx').on(t.processed),
	]
)

/**
 * Activities - Unified view of all customer interactions
 * References communication tables (CallLogs, SmsMessages, EmailMessages)
 * Adds business context for CRM (leads, opportunities, outcomes)
 */
export const Activities = sqliteTable(
	'activities',
	{
		id: integer('id').primaryKey({ autoIncrement: true }),
		uuid: text('uuid')
			.unique()
			.$defaultFn(() => generateUuid())
			.notNull(),
		workspace_id: text('workspace_id').notNull(),

		// CRM relationships
		contact_id: integer('contact_id'),
		opportunity_id: integer('opportunity_id'),
		order_id: integer('order_id'),

		// Activity type and polymorphic reference
		activity_type: text('activity_type', {
			enum: ['call', 'sms', 'email'],
		}).notNull(),

		// Polymorphic references to communication records
		call_log_id: integer('call_log_id'),
		sms_message_id: integer('sms_message_id'),
		email_message_id: integer('email_message_id'),

		// Business metadata
		subject: text('subject'),
		description: text('description'),
		outcome: text('outcome', {
			enum: ['completed', 'scheduled', 'cancelled', 'no-answer', 'voicemail', 'qualified', 'not-qualified'],
		}),

		// Assignment
		assigned_to_user_id: text('assigned_to_user_id'),
		completed_by_user_id: text('completed_by_user_id'),

		// Timestamps
		scheduled_at: text('scheduled_at'),
		completed_at: text('completed_at'),
		created_at: text('created_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	},
	(t) => [
		index('activities_workspace_idx').on(t.workspace_id, t.created_at),
		index('activities_contact_idx').on(t.contact_id),
		index('activities_opportunity_idx').on(t.opportunity_id),
		index('activities_type_idx').on(t.activity_type),
		index('activities_call_idx').on(t.call_log_id),
		index('activities_sms_idx').on(t.sms_message_id),
		index('activities_email_idx').on(t.email_message_id),
	]
)

/**
 * Domains table for workspace subdomains and custom domains
 * - Subdomain: Auto-assigned on workspace creation (e.g., acme-corp.tryhoneygrid.com)
 * - Custom: User-purchased domains via Cloudflare registrar (e.g., www.acme.com)
 */
export const Domains = sqliteTable('domains', {
	id: integer('id').primaryKey({ autoIncrement: true }),
	uuid: text('uuid')
		.unique()
		.$defaultFn(() => generateUuid())
		.notNull(),
	workspace_id: text('workspace_id').notNull(),

	// Domain type and pattern
	type: text('type', { enum: ['subdomain', 'custom'] }).notNull(),
	pattern: text('pattern').notNull(), // The actual subdomain or custom domain

	// Cloudflare integration (for custom domains)
	cloudflare_zone_id: text('cloudflare_zone_id'),
	cloudflare_registrar_id: text('cloudflare_registrar_id'),

	// DNS and verification
	verification_status: text('verification_status', {
		enum: ['pending', 'verified', 'failed', 'expired']
	}).notNull().default('pending'),
	verification_token: text('verification_token'),
	dns_records: text('dns_records', { mode: 'json' }),

	// Status and metadata
	is_primary: integer('is_primary', { mode: 'boolean' }).notNull().default(false),
	is_active: integer('is_active', { mode: 'boolean' }).notNull().default(true),
	expires_at: text('expires_at'),

	// Audit fields
	created_at: text('created_at')
		.$defaultFn(() => new Date().toISOString())
		.notNull(),
	updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	verified_at: text('verified_at'),
})

/**
 * Contacts table - stores person/lead information
 * Represents a person and their contact information (email, phone, name)
 * Activities are linked to contacts to track all interactions
 */
export const Contacts = sqliteTable(
	'contacts',
	{
		id: integer('id').primaryKey({ autoIncrement: true }),
		uuid: text('uuid')
			.unique()
			.$defaultFn(() => generateUuid())
			.notNull(),
		workspace_id: text('workspace_id').notNull(),

		// Contact identification
		email: text('email'),
		phone_number: text('phone_number'),
		name: text('name'),

		// Contact status and assignment
		status: text('status', { enum: ['new', 'contacted', 'qualified', 'converted', 'lost'] })
			.notNull()
			.default('new'),
		assigned_to: text('assigned_to'), // member_id from Stytch

		// Custom data and notes
		custom_data: text('custom_data', { mode: 'json' }),
		notes: text('notes'),

		// Timestamps
		first_contact_at: text('first_contact_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		last_contact_at: text('last_contact_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		created_at: text('created_at')
			.$defaultFn(() => new Date().toISOString())
			.notNull(),
		updated_at: text('updated_at').$onUpdateFn(() => new Date().toISOString()),
	},
	(t) => [
		index('contacts_workspace_idx').on(t.workspace_id, t.created_at),
		index('contacts_status_idx').on(t.status),
		index('contacts_email_idx').on(t.email),
		index('contacts_phone_idx').on(t.phone_number),
	]
)

// Define relationships
export const locationsRelations = relations(Locations, ({ many }) => ({
	opening_hours: many(OpeningHoursPeriods),
	photos: many(Photos),
	reviews: many(Reviews),
}))

export const gridsRelations = relations(Grids, ({ many }) => ({
	gridDataSources: many(GridDataSources),
}))

export const dataSourcesRelations = relations(DataSources, ({ many, one }) => ({
	gridDataSources: many(GridDataSources),
	versions: many(DataSourceVersions),
	connection: one(Connections, {
		fields: [DataSources.connection_id],
		references: [Connections.id],
	}),
}))

export const connectionsRelations = relations(Connections, ({ many }) => ({
	dataSources: many(DataSources),
}))

export const dataSourceVersionsRelations = relations(DataSourceVersions, ({ one }) => ({
	dataSource: one(DataSources, {
		fields: [DataSourceVersions.data_source_id],
		references: [DataSources.id],
	}),
}))

export const gridDataSourcesRelations = relations(GridDataSources, ({ one }) => ({
	grid: one(Grids, {
		fields: [GridDataSources.grid_id],
		references: [Grids.id],
	}),
	dataSource: one(DataSources, {
		fields: [GridDataSources.data_source_id],
		references: [DataSources.id],
	}),
}))

export const gridVersionsRelations = relations(GridVersions, ({ many, one }) => ({
	grid: one(Grids, {
		fields: [GridVersions.grid_id],
		references: [Grids.id],
	}),
	dataSources: many(GridVersionDataSources),
}))

export const gridVersionDataSourcesRelations = relations(GridVersionDataSources, ({ one }) => ({
	gridVersion: one(GridVersions, {
		fields: [GridVersionDataSources.grid_version_id],
		references: [GridVersions.id],
	}),
	dataSource: one(DataSources, {
		fields: [GridVersionDataSources.data_source_id],
		references: [DataSources.id],
	}),
	dataSourceVersion: one(DataSourceVersions, {
		fields: [GridVersionDataSources.data_source_version_id],
		references: [DataSourceVersions.id],
	}),
}))

export const workspaceSchema = {
	Grids,
	GridVersions,
	GridVersionDataSources,
	GridDataSources,
	Locations,
	OpeningHoursPeriods,
	Photos,
	Reviews,
	DataSources,
	DataSourceVersions,
	Connections,
	PhoneNumbers,
	CallLogs,
	CallRecordings,
	SmsMessages,
	EmailMessages,
	WebhookEvents,
	Activities,
	Domains,
	Contacts,
} as const
